// SECȚIUNEA PRIN CLĂDIRE: un plan care taie modelul și ascunde ce e dincoace
// de el.
//
// De ce e nevoie de ea. Scena arată clădirea din afară, iar tot ce e
// interesant la o structură — cum intră grinda în stâlp, unde se oprește
// peretele de subsol, cum se țese armătura într-un nod — e ÎNĂUNTRU.
// Layerul «Realist» rezolvă asta pe jumătate, făcând betonul translucid, dar
// la o clădire cu o mie de piese privirea trece prin cinci ziduri și ce se
// vede e un ghem. Secțiunea rezolvă cealaltă jumătate: taie și ARUNCĂ ce e
// între ochi și locul la care te uiți.
//
// ---------------------------------------------------------------------------
// CE FAC CELELALTE PROGRAME, ȘI CE AM LUAT DE LA FIECARE
// ---------------------------------------------------------------------------
//
//   * REVIT — «section box»: o cutie cu șase fețe, fiecare cu un mâner tras
//     cu mausul. Fața tăiată se umple cu HAȘURA DE SECȚIUNE a materialului,
//     nu rămâne goală. De aici am luat lucrul cel mai important: TĂIETURA SE
//     UMPLE (v. „capacul", mai jos). O cutie cu șase fețe e însă mai mult
//     decât trebuie aici — nouăzeci la sută din întrebări se răspund cu un
//     singur plan.
//   * NAVISWORKS — «sectioning»: planuri (până la șase) sau cutie, fiecare
//     aliniat pe o direcție principală, cu un gizmo de mutat. NU umple
//     tăietura: elementele ciopârțite apar goale pe dinăuntru, și de aceea
//     un stâlp tăiat arată ca o țeavă. Am luat alinierea pe direcțiile
//     principale și faptul că planul se vede ca un CADRU desenat peste
//     model; am lăsat tăietura goală.
//   * SKETCHUP — «section plane»: planul e un obiect în model, cu două
//     comutatoare separate — „arată tăietura" și „umple tăietura". Umplerea
//     e o singură culoare plată pentru tot, nu una per material. Am luat
//     exact asta: o culoare de tăietură, plată, neafectată de lumină.
//   * FORGE / APS VIEWER, SOLIBRI, BIMCOLLAB — buton X / Y / Z, un cursor
//     și un buton de întors sensul. Am luat numele axelor (X, Y, Z sunt deja
//     în capul structuristului) și cursorul, fiindcă e singurul gest care
//     dă și o poziție EXACTĂ, nu doar una nimerită cu mausul.
//   * ETABS / ROBOT — acolo „secțiunea" e de fapt o limitare pe niveluri.
//     De aici vine legarea cursorului de COTELE DE NIVEL: la o tăietură
//     orizontală, planul se prinde de cotele etajelor, fiindcă exact acolo
//     vrea structuristul să taie.
//
// ---------------------------------------------------------------------------
// CUM E FĂCUTĂ
// ---------------------------------------------------------------------------
//
// 1. TĂIEREA e a plăcii video: `renderer.clippingPlanes`. Un plan global, nu
//    unul pus pe fiecare material: scena își construiește materialele în
//    treizeci de locuri (și încă unul asincron, armătura), iar un plan pus pe
//    material ar trebui dus în toate. Global, orice ajunge în scenă de acum
//    încolo e tăiat din oficiu.
//
// 2. UMPLEREA TĂIETURII («capacul») se face cu STENCIL, fiindcă tăierea nu
//    lasă o suprafață în urmă — ea doar aruncă pixeli, deci în locul
//    betonului rămâne o gaură prin care se vede interiorul cutiei. Trucul e
//    cel clasic (three.js, exemplul `webgl_clipping_stencil`): pentru fiecare
//    familie de volume se desenează geometria de DOUĂ ori, fără culoare și
//    fără adâncime — o dată numai fețele din spate, care CRESC stencilul, o
//    dată numai cele din față, care îl SCAD. Acolo unde raza a intrat într-un
//    corp și n-a mai ieșit (fiindcă ieșirea a fost tăiată), numărătoarea
//    rămâne nenulă. Peste tot se trage apoi un singur dreptunghi care se
//    desenează NUMAI unde stencilul e nenul — adică exact pe fața tăiată.
//    Reuniunea a două corpuri suprapuse iese tot nenulă, deci un stâlp
//    înecat într-un perete se umple o singură dată, fără găuri.
//
//    Geometria de stencil e LIPITĂ pe familii: un singur obiect per grup, nu
//    două pe fiecare piesă. La o clădire cu o mie de piese diferența e între
//    douăzeci de desene în plus pe cadru și două mii. Fiind copil al
//    grupului, urmează singură bifa din „ce se vede": ce e stins nu se umple.
//
// 3. CULOAREA TĂIETURII e `PAL.beton` — culoarea betonului din SECȚIUNILE
//    DESENATE ale pașilor, nu din familiile 3D. Tăietura vorbește limba
//    planșei: pe o planșă betonul tăiat e o pată deschisă, uniformă, iar
//    faptul că e stâlp sau grindă îl spune conturul din jur, nu umplutura.
//    De asta e și PLATĂ (`MeshBasicMaterial`, `toneMapped: false`): o față
//    tăiată nu e o față reală a corpului, deci n-are de ce fi luminată — și
//    iese la fel în amândouă temele, deși una are tone mapping și cealaltă nu.
//
// 4. TERENUL se taie, dar NU se umple. Prisma de pământ nu e un corp închis:
//    înăuntrul ei stau semnele de hașură (liniuțele de argilă, punctele de
//    nisip), care sunt suprafețe deschise și ar strica numărătoarea de
//    stencil. Tăiată și nelăsată cu capac, ea arată exact ca o secțiune
//    geotehnică: straturile deschise, cu semnele lor la vedere.

import * as THREE from "three";
import { PAL3D } from "./paletaDesen";

export type AxaSectiune = "x" | "y" | "z";

export interface StareSectiune {
  /** pe ce axă a GRILEI e perpendicular planul de tăiere */
  axa: AxaSectiune;
  /** cota planului pe axa lui, în metri (pentru «z» chiar cota de nivel) */
  t: number;
  /** ce jumătate rămâne: `false` = cea cu coordonata mai MARE decât `t` */
  invers: boolean;
}

export interface Interval { min: number; max: number }
export type Limite = Record<AxaSectiune, Interval>;

export const AXE: AxaSectiune[] = ["x", "y", "z"];

/** Celelalte două axe, cele care întind planul de tăiere. */
export const IN_PLAN: Record<AxaSectiune, [AxaSectiune, AxaSectiune]> = {
  x: ["y", "z"], y: ["x", "z"], z: ["x", "y"],
};

/** Direcția unei axe a GRILEI, în coordonatele lumii. Grila e rotită cu
 *  `theta` (Pasul 2, `model.theta_rad`) — aceeași convenție ca în Ochi3D:
 *  X-ul grilei e (cos θ, sin θ), Y-ul e (−sin θ, cos θ). Fără ea, pe o
 *  clădire rotită cu 27° secțiunea „pe X" ar tăia oblic prin toți pereții. */
export function directiaAxei(axa: AxaSectiune, theta: number): THREE.Vector3 {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  return axa === "z" ? new THREE.Vector3(0, 0, 1)
    : axa === "x" ? new THREE.Vector3(c, s, 0)
      : new THREE.Vector3(-s, c, 0);
}

/** Cât se întinde clădirea pe fiecare axă a grilei — capetele cursorului.
 *
 *  Se măsoară pe CUTIILE PIESELOR, nu pe cutia clădirii întregi: cutia
 *  întreagă e aliniată la axele lumii, iar proiectată pe o grilă rotită ar
 *  ieși cu câțiva metri mai lată decât clădirea, adică cursorul ar avea la
 *  ambele capete o zonă moartă în care nu se taie nimic. `visible` se
 *  IGNORĂ: capetele cursorului n-au voie să sară când omul stinge un layer. */
export function limiteGrila(radacini: THREE.Object3D[], theta: number): Limite {
  const u = { x: directiaAxei("x", theta), y: directiaAxei("y", theta),
              z: directiaAxei("z", theta) };
  const lim: Limite = {
    x: { min: Infinity, max: -Infinity },
    y: { min: Infinity, max: -Infinity },
    z: { min: Infinity, max: -Infinity },
  };
  const v = new THREE.Vector3();
  for (const rad of radacini) {
    rad.updateWorldMatrix(true, true);
    rad.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry || o.userData?.stencil) return;
      if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
      const bb = m.geometry.boundingBox;
      if (!bb || bb.isEmpty()) return;
      for (let i = 0; i < 8; i++) {
        v.set(i & 1 ? bb.max.x : bb.min.x,
              i & 2 ? bb.max.y : bb.min.y,
              i & 4 ? bb.max.z : bb.min.z).applyMatrix4(m.matrixWorld);
        for (const a of AXE) {
          const t = v.dot(u[a]);
          if (t < lim[a].min) lim[a].min = t;
          if (t > lim[a].max) lim[a].max = t;
        }
      }
    });
  }
  // o scenă fără volume (rulare veche, model gol): un interval degenerat, ca
  // împărțirile din cursor să nu dea NaN
  for (const a of AXE)
    if (!isFinite(lim[a].min) || lim[a].max - lim[a].min < 1e-6) {
      lim[a].min = 0; lim[a].max = 1;
    }
  return lim;
}

/** Jumătatea care trebuie ARUNCATĂ ca tăietura să fie cu fața la om: cea
 *  dinspre cameră. Fără asta, jumătate din aprinderi taie exact pe dos și
 *  omul vede spatele clădirii, nu interiorul ei. */
export function inversPentruCamera(axa: AxaSectiune, theta: number,
                                   spreCamera: THREE.Vector3): boolean {
  // `invers = false` păstrează jumătatea cu coordonata MARE, adică o aruncă
  // pe cea dinspre −u. Ea trebuie aleasă când camera stă pe partea aceea.
  return directiaAxei(axa, theta).dot(spreCamera) > 0;
}

// ---------------------------------------------------------------------------
// CE SE POATE ALEGE CU MAUSUL DINTR-O CLĂDIRE TĂIATĂ
// ---------------------------------------------------------------------------
//
// Tăierea e a plăcii video: ea aruncă PIXELI, iar raza de click nu știe
// nimic despre ea (`Raycaster` nu se uită la `clippingPlanes` — nici la cele
// globale, nici la cele de pe material). Fără ce urmează, clickul alege prima
// piesă de pe rază, adică tocmai fațada pe care secțiunea a aruncat-o: omul
// se uită prin clădire și selectează zidul prin care se uită.
//
// Și nu e destul să arunci loviturile din jumătatea tăiată: pe fața tăiată se
// vede CAPACUL, iar capacul e o piesă adevărată — peretele prin care tocmai
// ai trecut. Materialele scenei sunt `FrontSide`, deci raza intră în perete
// pe fața dinspre om (lovitură în jumătatea aruncată, deci scoasă) și nu mai
// iese niciodată: peretele tăiat ar deveni de neatins, iar clickul pe el ar
// alege ce e în SPATELE lui.
//
// De asta clickul se judecă în două:
//
//   * loviturile RĂMASE — cele de dincolo de plan, suprafețe care chiar se
//     văd;
//   * piesele de sub CAPAC — cele în care punctul în care raza străpunge
//     planul de tăiere chiar stă. Ele sunt ce se vede sub cursor, la
//     distanța planului, adică ÎNAINTEA oricărei lovituri rămase.
//
// „Punctul e în piesă" se răspunde numărând străpungerile: raza iese dintr-un
// corp închis de un număr impar de ori dacă a pornit dinăuntru. Se numără pe
// triunghiuri, în coordonatele piesei, și numai pentru piesele pe care raza
// le-a lovit deja în jumătatea aruncată — adică cel mult câteva la un click.

/** Cât de departe de plan trebuie să fie o lovitură ca să fie de partea
 *  păstrată. Sub o zecime de milimetru e zgomot de float pe o clădire de
 *  zeci de metri, nu o față adevărată. */
const EPS_PLAN = 1e-4;

/** Planul de tăiere al unei stări, în coordonatele lumii: se păstrează
 *  jumătatea cu n·p + c ≥ 0. Aceeași socoteală o face și `aseaza`, pe planul
 *  dat plăcii video — de asta stă într-un singur loc. */
export function planTaiere(s: StareSectiune, theta: number,
                           tinta = new THREE.Plane()): THREE.Plane {
  const dir = directiaAxei(s.axa, theta);
  tinta.normal.copy(s.invers ? dir.negate() : dir);
  tinta.constant = s.invers ? s.t : -s.t;
  return tinta;
}

/** A rămas ceva pe ecran din piesa asta după tăiere? Se măsoară pe CUTIA ei,
 *  deci răspunsul e generos: la un stâlp în L cutia poate supraviețui cu un
 *  colț gol. Generozitatea e cea bună — greșeala ieftină e să ții selecția pe
 *  o piesă care abia se mai vede, nu s-o pierzi pe una care se vede. */
export function seVedeDupaTaiere(m: THREE.Mesh, plan: THREE.Plane): boolean {
  if (!m.geometry) return true;
  if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
  const bb = m.geometry.boundingBox;
  if (!bb || bb.isEmpty()) return true;
  m.updateWorldMatrix(true, false);
  const v = new THREE.Vector3();
  for (let i = 0; i < 8; i++) {
    v.set(i & 1 ? bb.max.x : bb.min.x,
          i & 2 ? bb.max.y : bb.min.y,
          i & 4 ? bb.max.z : bb.min.z).applyMatrix4(m.matrixWorld);
    if (plan.distanceToPoint(v) >= -EPS_PLAN) return true;
  }
  return false;
}

/** Stă punctul `p` ÎN piesă? Se numără străpungerile razei dincolo de el:
 *  impar = a pornit dinăuntru. Raza e cea a clickului, deci e și direcția în
 *  care ochiul chiar se uită. */
export function punctulEInPiesa(m: THREE.Mesh, raza: THREE.Ray,
                                p: THREE.Vector3): boolean {
  const geo = m.geometry;
  const poz = geo?.getAttribute("position") as THREE.BufferAttribute | undefined;
  if (!poz) return false;
  m.updateWorldMatrix(true, false);
  const inv = new THREE.Matrix4().copy(m.matrixWorld).invert();
  const r = raza.clone().applyMatrix4(inv);       // direcția rămâne unitară
  const tTaiere = r.direction.dot(p.clone().applyMatrix4(inv).sub(r.origin));
  const idx = geo.index;
  const n = idx ? idx.count : poz.count;
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const lovit = new THREE.Vector3();
  let strapungeri = 0;
  for (let i = 0; i + 2 < n; i += 3) {
    const i0 = idx ? idx.getX(i) : i;
    const i1 = idx ? idx.getX(i + 1) : i + 1;
    const i2 = idx ? idx.getX(i + 2) : i + 2;
    a.fromBufferAttribute(poz, i0);
    b.fromBufferAttribute(poz, i1);
    c.fromBufferAttribute(poz, i2);
    // fără cullingul fețelor: se numără și intrările, și ieșirile
    if (!r.intersectTriangle(a, b, c, false, lovit)) continue;
    if (r.direction.dot(lovit.sub(r.origin)) > tTaiere + EPS_PLAN) strapungeri++;
  }
  return strapungeri % 2 === 1;
}

export interface LaTaietura {
  /** loviturile de dincolo de plan, în ordinea razei: suprafețe care se văd */
  ramase: THREE.Intersection[];
  /** piesele al căror capac se vede CHIAR sub cursor, în ordinea razei */
  capac: THREE.Object3D[];
}

/** Ce se vede sub cursor într-o clădire tăiată. `areCapac` spune care piese
 *  se umplu la tăietură: golurile (ferestrele, ușile) și terenul nu, deci
 *  prin ele se vede mai departe, nu se oprește clickul.
 *
 *  CAPACUL NU E MEREU ÎN FAȚĂ. Omul se poate roti de partea PĂSTRATĂ: atunci
 *  planul îi rămâne dincolo de clădire, fața tăiată e cu spatele (și e
 *  acoperită de adâncime, deci nici nu se desenează), iar ce se vede e
 *  fațada, întreagă. De asta capacul se ia în seamă numai când planul e mai
 *  aproape decât prima suprafață rămasă — adică exact când se și vede. */
export function laTaietura(lovite: THREE.Intersection[], raza: THREE.Ray,
                           plan: THREE.Plane,
                           areCapac: (o: THREE.Object3D) => boolean): LaTaietura {
  const ramase = lovite.filter(
    (h) => plan.distanceToPoint(h.point) >= -EPS_PLAN);
  const pTaiere = raza.intersectPlane(plan, new THREE.Vector3());
  const capac: THREE.Object3D[] = [];
  const panaLaPlan = pTaiere ? raza.origin.distanceTo(pTaiere) : Infinity;
  if (pTaiere && panaLaPlan <= (ramase[0]?.distance ?? Infinity) + EPS_PLAN) {
    for (const h of lovite) {
      const m = h.object as THREE.Mesh;
      if (plan.distanceToPoint(h.point) >= -EPS_PLAN) continue;
      if (capac.includes(m) || !areCapac(m) || !m.isMesh) continue;
      if (punctulEInPiesa(m, raza, pTaiere)) capac.push(m);
    }
  }
  return { ramase, capac };
}

export interface Sectiune3D {
  /** Așază secțiunea; `null` o stinge. */
  aseaza(s: StareSectiune | null): void;
  dispose(): void;
}

/** Cât intră capacul în jumătatea PĂSTRATĂ, ca planul global să nu-l taie
 *  chiar pe el (stă fix în planul de tăiere, adică exact pe muchia deciziei).
 *  Relativ la mărimea clădirii: la un bloc de 40 m înseamnă 2 mm — peste
 *  zgomotul de rotunjire al unui float, sub orice se poate vedea. */
const RETRAGERE = 5e-5;

// ---- ORDINEA DE DESENARE, ȘI CAPCANA EI ------------------------------------
//
// Capacul TREBUIE desenat după trecerile de stencil — altfel se croiește pe o
// numărătoare care încă nu s-a făcut, adică nu se desenează nicăieri.
//
// Și aici e capcana: în three, `renderOrder` NU e ultimul cuvânt. Sortarea
// compară întâi `groupOrder` — `renderOrder`-ul GRUPULUI care îl conține (v.
// `projectObject`: „if (object.isGroup) groupOrder = object.renderOrder") —
// și abia la egalitate se uită la `renderOrder`-ul piesei. Iar scena asta
// pune un `renderOrder` pe fiecare grup de familie (`asaza`, în `StepFem`),
// ca amestecul translucidelor să fie așezat, nu tras la sorți.
//
// Cu capacul agățat DIRECT de scenă, `groupOrder`-ul lui e 0 — deci se
// desena înaintea a tot, oricât de mare i-ar fi fost `renderOrder`-ul.
// Trecerile de stencil, fiind copii ai grupurilor de familie, moșteneau
// `groupOrder` 100000–900000 și veneau DUPĂ el. Rezultatul: fața tăiată
// rămânea goală, fără nicio eroare nicăieri.
//
// De asta capacul și cadrul stau într-un grup al lor, cu `renderOrder`-ul
// peste orice familie (`ORD` din `StepFem` urcă până pe la 10 × 100000).
const ORD_GIZMO = 9_000_000;
/** Trecerile de stencil se așază în CODA grupului lor de familie: între ele
 *  ordinea nu contează (nu scriu nici culoare, nici adâncime), dar în urma
 *  pieselor familiei se citește mai ușor la depanare. */
const ORD_STENCIL = 9_000_000;

/** Geometria unui grup întreg, lipită într-una singură, în coordonatele
 *  LUMII. Numai `position`: stencilul nu are nevoie nici de normale, nici de
 *  coordonate de textură, iar ele ar dubla memoria degeaba. */
function lipeste(grup: THREE.Object3D): THREE.BufferGeometry | null {
  const parti: Float32Array[] = [];
  let n = 0;
  const v = new THREE.Vector3();
  grup.updateWorldMatrix(true, true);
  grup.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.geometry || o.userData?.stencil) return;
    const g = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry;
    const p = g.getAttribute("position") as THREE.BufferAttribute | undefined;
    if (p) {
      const arr = new Float32Array(p.count * 3);
      for (let i = 0; i < p.count; i++) {
        v.fromBufferAttribute(p, i).applyMatrix4(m.matrixWorld);
        arr[i * 3] = v.x; arr[i * 3 + 1] = v.y; arr[i * 3 + 2] = v.z;
      }
      parti.push(arr);
      n += arr.length;
    }
    if (g !== m.geometry) g.dispose();
  });
  if (!n) return null;
  const tot = new Float32Array(n);
  let k = 0;
  for (const a of parti) { tot.set(a, k); k += a.length; }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(tot, 3));
  return geo;
}

/** O trecere de stencil: geometria familiei, fără culoare și fără adâncime,
 *  numărând fețele de un singur sens. */
function fataStencil(geo: THREE.BufferGeometry, fata: THREE.Side,
                     op: THREE.StencilOp): THREE.Mesh {
  const mat = new THREE.MeshBasicMaterial({
    side: fata,
    colorWrite: false,
    depthWrite: false,
    // fără test de adâncime: se numără TOATE fețele de pe rază, nu doar cea
    // mai apropiată — altfel corpurile din spate n-ar mai intra în socoteală
    depthTest: false,
    stencilWrite: true,
    stencilFunc: THREE.AlwaysStencilFunc,
    stencilFail: op,
    stencilZFail: op,
    stencilZPass: op,
  });
  const m = new THREE.Mesh(geo, mat);
  m.renderOrder = ORD_STENCIL;
  m.userData.stencil = true;
  // geometria e deja în coordonatele lumii: matricea proprie trebuie să
  // desfacă transformarea grupului-părinte (azi identitate peste tot, dar
  // asta nu e o promisiune pe care s-o putem cere scenei)
  m.matrixAutoUpdate = false;
  m.matrixWorldNeedsUpdate = true;
  return m;
}

export function creeazaSectiune(opts: {
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  /** unghiul grilei (`model.theta_rad`) */
  theta: number;
  /** grupurile de VOLUME închise care se umplu la tăietură */
  volume: THREE.Object3D[];
  /** limitele grilei, pentru mărimea cadrului și a capacului */
  limite: Limite;
}): Sectiune3D {
  const { scene, renderer, theta, volume, limite } = opts;
  const u = { x: directiaAxei("x", theta), y: directiaAxei("y", theta),
              z: directiaAxei("z", theta) };
  const mijloc = (a: AxaSectiune) => (limite[a].min + limite[a].max) / 2;
  const intindere = Math.max(
    limite.x.max - limite.x.min,
    limite.y.max - limite.y.min,
    limite.z.max - limite.z.min, 1);
  const retragere = intindere * RETRAGERE;

  const plan = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0);
  const PLANURI = [plan];
  const FARA: THREE.Plane[] = [];

  // ---- UMBRELE URMEAZĂ TĂIETURA ------------------------------------------
  //
  // Fără asta, jumătatea aruncată continuă să arunce umbră pe teren (layerul
  // realist): o pată de clădire care nu mai există, întinsă lângă una care
  // există pe jumătate.
  //
  // Și nu se rezolvă din planul global. La desenarea hărții de umbră, three
  // STINGE planurile globale — `WebGLClipping.beginShadows()` cheamă
  // `projectPlanes(null)`, fără nicio condiție. Singurele care supraviețuiesc
  // pasului de umbră sunt planurile puse PE MATERIAL, și numai cu
  // `localClippingEnabled` pornit și `clipShadows` pe material.
  //
  // Deci cine aruncă umbră primește ACELAȘI plan și pe material. În pasul
  // normal cele două se adună (aceeași tăietură, spusă de două ori — nimic nu
  // se schimbă); în pasul de umbră rămâne numai cel de pe material, care e
  // tocmai ce trebuie. Se pune și se scoate la aprindere/stingere, nu la
  // fiecare mutare a planului: lungimea listei e cea care cere recompilarea
  // shaderului, iar ea se schimbă o singură dată.
  renderer.localClippingEnabled = true;
  const umbrele = (planuri: THREE.Plane[] | null) => {
    scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.castShadow) return;
      const mat = m.material;
      (Array.isArray(mat) ? mat : mat ? [mat] : []).forEach((x) => {
        x.clipShadows = true;
        x.clippingPlanes = planuri;
      });
    });
  };

  const gunoi: { dispose(): void }[] = [];
  let gizmo: THREE.Group | null = null;
  let capac: THREE.Mesh | null = null;
  let cadru: THREE.LineLoop | null = null;

  /** Piesele se construiesc la PRIMA aprindere: o scenă în care nimeni nu
   *  taie nimic n-are de ce să plătească lipirea geometriei. */
  function cladeste() {
    if (gizmo) return;
    for (const g of volume) {
      const geo = lipeste(g);
      if (!geo) continue;
      gunoi.push(geo);
      const inv = new THREE.Matrix4().copy(g.matrixWorld).invert();
      for (const [fata, op] of [
        [THREE.BackSide, THREE.IncrementWrapStencilOp],
        [THREE.FrontSide, THREE.DecrementWrapStencilOp],
      ] as [THREE.Side, THREE.StencilOp][]) {
        const m = fataStencil(geo, fata, op);
        m.matrix.copy(inv);
        gunoi.push(m.material as THREE.Material);
        g.add(m);
      }
    }

    // CAPACUL: un singur dreptunghi peste toată tăietura. E mai mare decât
    // clădirea fiindcă stencilul îl croiește oricum — mărimea lui nu se vede,
    // doar lipsa ei s-ar vedea, ca un colț de tăietură rămas gol.
    const l = intindere * 1.6;
    const geoCapac = new THREE.PlaneGeometry(l, l);
    const matCapac = new THREE.MeshBasicMaterial({
      color: PAL3D.beton,
      toneMapped: false,
      side: THREE.DoubleSide,
      stencilWrite: true,
      stencilRef: 0,
      stencilFunc: THREE.NotEqualStencilFunc,
      // după ce a desenat, capacul pune stencilul la loc pe 0
      stencilFail: THREE.ReplaceStencilOp,
      stencilZFail: THREE.ReplaceStencilOp,
      stencilZPass: THREE.ReplaceStencilOp,
    });
    gizmo = new THREE.Group();
    gizmo.renderOrder = ORD_GIZMO;
    scene.add(gizmo);

    capac = new THREE.Mesh(geoCapac, matCapac);
    capac.renderOrder = 1;
    capac.frustumCulled = false;
    capac.userData.adnotare = true;   // nu intră în încadrarea camerei
    // stencilul se pune la loc pe 0 imediat după ce capacul l-a folosit:
    // altfel rămâne scris peste cadrul următor
    capac.onAfterRender = (r) => r.clearStencil();
    gunoi.push(geoCapac, matCapac);
    gizmo.add(capac);

    // CADRUL: unde stă planul, desenat peste model (`depthTest: false`) ca
    // să se vadă și când a ieșit din clădire. Accentul interfeței — e o
    // unealtă, nu o piesă de structură, deci n-are voie să ia culoarea
    // niciunei familii.
    const geoCadru = new THREE.BufferGeometry();
    geoCadru.setAttribute("position",
      new THREE.BufferAttribute(new Float32Array(12), 3));
    const matCadru = new THREE.LineBasicMaterial({
      color: PAL3D.acc, transparent: true, opacity: 0.85,
      depthTest: false, depthWrite: false, toneMapped: false,
    });
    cadru = new THREE.LineLoop(geoCadru, matCadru);
    cadru.renderOrder = 2;            // peste capac, în același grup
    cadru.frustumCulled = false;
    cadru.userData.adnotare = true;
    gunoi.push(geoCadru, matCadru);
    gizmo.add(cadru);
  }

  const aseaza = (s: StareSectiune | null) => {
    if (!s) {
      renderer.clippingPlanes = FARA;
      umbrele(null);
      if (gizmo) gizmo.visible = false;
      return;
    }
    cladeste();
    const dir = u[s.axa];
    // se păstrează n·p + c ≥ 0: cu `invers` fals asta e u·p ≥ t. Aceeași
    // socoteală o cere și clickul, ca să știe ce a mai rămas pe ecran — de
    // asta stă în `planTaiere`, nu aici.
    planTaiere(s, theta, plan);
    const n = plan.normal.clone();
    renderer.clippingPlanes = PLANURI;
    umbrele(PLANURI);

    const [a1, a2] = IN_PLAN[s.axa];
    // punctul din planul de tăiere care stă în dreptul mijlocului clădirii
    const centru = new THREE.Vector3()
      .addScaledVector(dir, s.t)
      .addScaledVector(u[a1], mijloc(a1))
      .addScaledVector(u[a2], mijloc(a2))
      .addScaledVector(n, retragere);

    if (gizmo) gizmo.visible = true;
    if (capac) {
      capac.position.copy(centru);
      capac.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), n);
    }
    if (cadru) {
      // dreptunghiul cât clădirea, cu puțin aer în jur: un cadru lipit de
      // muchii s-ar confunda cu conturul clădirii
      const aer = (a: AxaSectiune) =>
        Math.max((limite[a].max - limite[a].min) * 0.06, 0.4);
      const p = (cadru.geometry.getAttribute("position") as THREE.BufferAttribute);
      const colt = new THREE.Vector3();
      const semne: [number, number][] = [[0, 0], [1, 0], [1, 1], [0, 1]];
      semne.forEach(([i, j], k) => {
        const e1 = i ? limite[a1].max + aer(a1) : limite[a1].min - aer(a1);
        const e2 = j ? limite[a2].max + aer(a2) : limite[a2].min - aer(a2);
        colt.set(0, 0, 0)
          .addScaledVector(dir, s.t)
          .addScaledVector(u[a1], e1)
          .addScaledVector(u[a2], e2)
          .addScaledVector(n, retragere);
        p.setXYZ(k, colt.x, colt.y, colt.z);
      });
      p.needsUpdate = true;
      cadru.geometry.computeBoundingSphere();
    }
  };

  return {
    aseaza,
    dispose: () => {
      renderer.clippingPlanes = FARA;
      umbrele(null);
      gizmo?.removeFromParent();
      for (const g of volume)
        g.children.filter((c) => c.userData?.stencil)
          .forEach((c) => c.removeFromParent());
      gunoi.forEach((x) => x.dispose());
      gizmo = null;
      capac = null;
      cadru = null;
    },
  };
}
